import { RadioGroup as RadioGroupPrimitive } from "radix-ui"
import type { ComponentProps, ReactNode } from "react"
import { cn } from "cn"

export type RadioGroupProps = ComponentProps<typeof RadioGroupPrimitive.Root>

function RadioGroup({ className, ...props }: RadioGroupProps) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={cn("flex flex-col gap-3", className)}
      {...props}
    />
  )
}

type RadioGroupItemCommonProps = Omit<
  ComponentProps<typeof RadioGroupPrimitive.Item>,
  "children"
> & {
  label?: ReactNode
  description?: ReactNode
  showDescription?: boolean
}

export type RadioGroupItemDefaultProps = RadioGroupItemCommonProps & {
  /** Figma-Component-Set „Radio Button". */
  variant?: "default"
  wrapperClassName?: string
}

export type RadioGroupItemCardProps = RadioGroupItemCommonProps & {
  /** Figma-Component-Set „Choice Card". */
  variant: "card"
  icon?: ReactNode
  showIcon?: boolean
  /** Figmas eigene `variant`-Achse (horizontal/vertical) — hier `layout` genannt, da `variant` bereits als Diskriminator zwischen „Radio Button" und „Choice Card" dient. */
  layout?: "vertical" | "horizontal"
}

export type RadioGroupItemProps =
  RadioGroupItemDefaultProps | RadioGroupItemCardProps

function RadioGroupItem(props: RadioGroupItemProps) {
  const {
    className,
    disabled,
    label,
    description,
    showDescription = false,
    ...rest
  } = props

  if (rest.variant === "card") {
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- nur zur Diskriminierung des Props-Unions nötig
      variant,
      icon,
      showIcon = true,
      layout = "vertical",
      ...itemProps
    } = rest

    return (
      <RadioGroupPrimitive.Item
        data-slot="radio-group-item"
        data-variant="card"
        disabled={disabled}
        className={cn(
          "relative flex rounded-xl border-2 border-neutrals-v2-400 p-4 text-left transition-colors outline-none",
          "hover:border-neutrals-v2-400 hover:bg-neutrals-v2-200",
          "focus-visible:border-neutrals-v2-1000",
          "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-neutrals-v2-400 disabled:hover:bg-transparent",
          "data-[state=checked]:border-brand-blue-100 data-[state=checked]:bg-neutrals-v2-200",
          // Figma-Frames sind pro Zustand exakt content-groß (kein Unterschied zwischen `items-start` und `justify-center` sichtbar) — hier bewusst kein `justify-center`, damit Icon+Label oben bleiben, wenn ein Nachbar-Item durch längeres Label höher wird.
          layout === "vertical"
            ? "flex-col items-center gap-2 text-center"
            : "flex-row items-center gap-2",
          className
        )}
        {...itemProps}
      >
        {showIcon && icon && (
          <span
            data-slot="radio-group-item-icon"
            className={cn(
              "shrink-0 [&>svg]:size-full",
              layout === "vertical" ? "size-7" : "size-4.5"
            )}
          >
            {icon}
          </span>
        )}
        {(label || (showDescription && description)) && (
          <span
            data-slot="radio-group-item-content"
            className="flex flex-col gap-1"
          >
            {label && (
              <span className="text-sm leading-5 font-semibold text-labels-primary">
                {label}
              </span>
            )}
            {showDescription && description && (
              <span className="text-sm leading-5 text-neutrals-v2-700">
                {description}
              </span>
            )}
          </span>
        )}
      </RadioGroupPrimitive.Item>
    )
  }

  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- nur zur Diskriminierung des Props-Unions nötig
    variant,
    wrapperClassName,
    ...itemProps
  } = rest

  return (
    <label
      data-slot="radio-group-item-field"
      data-disabled={disabled || undefined}
      className={cn(
        "inline-flex items-start gap-2 p-0.5",
        disabled ? "cursor-not-allowed" : "cursor-pointer",
        wrapperClassName
      )}
    >
      <RadioGroupPrimitive.Item
        data-slot="radio-group-item"
        data-variant="default"
        disabled={disabled}
        className={cn(
          "relative flex size-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors outline-none",
          disabled
            ? "border-neutrals-v2-400"
            : "border-neutrals-v2-400 hover:border-neutrals-v2-600 focus-visible:border-neutrals-v2-1000 aria-invalid:border-error-red-200",
          disabled
            ? "data-[state=checked]:border-neutrals-v2-600"
            : "data-[state=checked]:border-brand-red-200",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        {...itemProps}
      >
        <RadioGroupPrimitive.Indicator
          data-slot="radio-group-item-indicator"
          className="flex items-center justify-center"
        >
          <span
            className={cn(
              "size-2.5 rounded-full",
              disabled ? "bg-neutrals-v2-600" : "bg-brand-red-200"
            )}
          />
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
      {(label || (showDescription && description)) && (
        <span
          data-slot="radio-group-item-content"
          className="flex min-w-0 flex-1 flex-col justify-center gap-2 text-sm leading-5 font-normal"
        >
          {label && (
            <span
              className={cn(
                "text-labels-primary",
                disabled && "text-labels-secondary"
              )}
            >
              {label}
            </span>
          )}
          {showDescription && description && (
            <span className="text-labels-secondary">{description}</span>
          )}
        </span>
      )}
    </label>
  )
}

export { RadioGroup, RadioGroupItem }
